import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { useAccess } from '../../../hooks/useAccess';
import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import { getCellReducers } from './helpers/getCellReducers';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import {
  useDeleteAiPrompt,
  useGetAiPrompts,
} from '../../../services/redux/api/appConfigApi';
import { IAiPrompt } from '../../../types/interfaces/ai-prompt';

export function useAiPromptsComponent(classes) {
  const { t } = useTranslation();
  const { data: aiPrompts } = useGetAiPrompts();
  const [itemForDelete, setItemForDelete] = useState<IAiPrompt | null>(null);
  const [itemForEditing, setItemForEditing] = useState<IAiPrompt | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteAiPrompt] = useDeleteAiPrompt();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);

  const filteredValues = useMemo(
    () =>
      aiPrompts?.filter((item: IAiPrompt) => {
        if (!filter) return true;
        const filterValue = filter.toLowerCase();
        return (
          item.key.toLowerCase().includes(filterValue) ||
          item.value.toLowerCase().includes(filterValue)
        );
      }),
    [aiPrompts, filter],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.aiPrompts?.canEdit ?? false,
      canDelete: access?.aiPrompts?.canDelete ?? false,
    },
    (item: IAiPrompt) => {
      setItemForEditing(item);
      setShowEditModal(true);
    },
    (item: IAiPrompt) => {
      setItemForDelete(item);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.AI_PROMPT_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    if (itemForDelete?.id) {
      const id = itemForDelete.id;
      setItemForDelete(null);
      deleteAiPrompt(id);
    }
  }

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
  };
}
